import { soccerUrl, trainingPath } from '../utils/paths';
import { getShareOrigin } from '../utils/canonicalOrigin';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const SYSTEM_ERROR_PATTERNS = [
  /cloudinary/i,
  /mongodb/i,
  /failed to upload/i,
  /failed to delete/i,
  /unexpected error/i,
  /REACT_APP_/i,
  /ALLOWED_ORIGINS/i,
  /railway/i,
  /vercel/i,
  /static resource/i,
  /cannot deserialize/i,
  /connection refused/i,
  /environment/i,
  /java\./i,
  /org\.springframework/i,
  /IOException/i,
];

function looksLikeSystemError(message) {
  if (!message || typeof message !== 'string') return false;
  return SYSTEM_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function sanitizeErrorMessage(status, apiMessage, correlationId) {
  if (status >= 500) {
    const ref = correlationId ? ` Reference: ${correlationId}` : '';
    return `Something went wrong on our end. Please try again later.${ref}`;
  }
  if (status === 401) {
    return 'Please sign in to continue.';
  }
  if (status === 403) {
    return "You don't have permission to do that.";
  }
  if (status === 404) {
    if (apiMessage && !looksLikeSystemError(apiMessage)) {
      return apiMessage;
    }
    return 'The requested item was not found.';
  }
  if (status === 413) {
    return apiMessage || 'File is too large. Please use an image under 10 MB.';
  }
  if (apiMessage && looksLikeSystemError(apiMessage)) {
    return 'Something went wrong. Please try again.';
  }
  return apiMessage || 'Something went wrong. Please try again.';
}

class ApiService {
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    console.log('API Request:', options.method || 'GET', url);
    console.log('API Base URL:', API_BASE_URL);
    
    const token = localStorage.getItem('authToken');
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);
      
      // Check if response has content
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = text ? { message: text } : { message: `Error: ${response.statusText}` };
      }

      if (!response.ok) {
        const rawMessage = data.message || data.error || `Error: ${response.statusText}`;
        if (
          rawMessage.includes('No static resource') ||
          (response.status === 404 && typeof rawMessage === 'string' && rawMessage.length > 200)
        ) {
          console.error('API misconfiguration: request may have reached the wrong server.', {
            url,
            apiBaseUrl: API_BASE_URL,
            status: response.status,
          });
        }
        if (response.status === 404 && endpoint.includes('/scrimmage-stats')) {
          console.error('Scrimmage stats endpoint not found.', { url, status: response.status });
        }
        const errorMessage = sanitizeErrorMessage(
          response.status,
          rawMessage,
          data.correlationId
        );
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      console.error('Failed URL:', url);
      console.error('API Base URL configured:', API_BASE_URL);
      
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        const apiUrl = API_BASE_URL.replace(/\/api\/?$/, '');
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        console.error('Network error: unable to reach API.', {
          url,
          apiBaseUrl: API_BASE_URL,
          apiUrl,
          origin,
          error,
        });
        throw new Error('Unable to connect. Please check your connection and try again.');
      }
      throw error;
    }
  }

  /** Appends viewerUserId so the API can mask player phone numbers for non-privileged viewers */
  withViewerQuery(url, viewerUserId) {
    if (viewerUserId == null || viewerUserId === '') return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}viewerUserId=${encodeURIComponent(viewerUserId)}`;
  }

  // Player APIs
  async registerPlayer(playerData) {
    return this.request('/players', {
      method: 'POST',
      body: playerData,
    });
  }

  async uploadPlayerProfilePicture(file) {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${API_BASE_URL}/players/upload-profile-picture`;
    const token = localStorage.getItem('authToken');

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      let data = {};
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { message: text || response.statusText };
      }

      if (!response.ok) {
        const rawMessage = data.message || data.error || `Upload failed (${response.status})`;
        throw new Error(sanitizeErrorMessage(response.status, rawMessage, data.correlationId));
      }

      if (!data.url) {
        throw new Error('Upload succeeded but no image URL was returned.');
      }

      return data.url;
    } catch (error) {
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        throw new Error('Photo upload failed. Try a smaller image or check your connection.');
      }
      throw error;
    }
  }

  async getPlayerById(id, viewerUserId) {
    return this.request(this.withViewerQuery(`/players/${id}`, viewerUserId));
  }

  async getMyPlayerProfile(userId) {
    return this.request(`/players/my-profile?userId=${encodeURIComponent(userId)}`);
  }

  async getMyRunnerProfile(userId) {
    return this.request(`/runners/my-profile?userId=${encodeURIComponent(userId)}`);
  }

  async getAccountProfile(userId) {
    return this.request(`/account/profile?userId=${encodeURIComponent(userId)}`);
  }

  async updateAccountProfile(userId, profileData) {
    return this.request(`/account/profile?userId=${encodeURIComponent(userId)}`, {
      method: 'PUT',
      body: profileData,
    });
  }

  async uploadAccountProfilePicture(file) {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${API_BASE_URL}/account/upload-profile-picture`;
    const token = localStorage.getItem('authToken');

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    let data = {};
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text || response.statusText };
    }

    if (!response.ok) {
      const rawMessage = data.message || data.error || `Upload failed (${response.status})`;
      throw new Error(sanitizeErrorMessage(response.status, rawMessage, data.correlationId));
    }

    if (!data.url) {
      throw new Error('Upload succeeded but no URL was returned');
    }
    return data.url;
  }

  async saveRunnerProfile(userId, profileData, { create = false } = {}) {
    return this.request(`/runners/my-profile?userId=${encodeURIComponent(userId)}`, {
      method: create ? 'POST' : 'PUT',
      body: profileData,
    });
  }

  async uploadRunnerProfilePicture(file) {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${API_BASE_URL}/runners/upload-profile-picture`;
    const token = localStorage.getItem('authToken');

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    let data = {};
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text || response.statusText };
    }

    if (!response.ok) {
      const rawMessage = data.message || data.error || `Upload failed (${response.status})`;
      throw new Error(sanitizeErrorMessage(response.status, rawMessage, data.correlationId));
    }

    if (!data.url) {
      throw new Error('Upload succeeded but no URL was returned');
    }
    return data.url;
  }

  async getMyClubMemberships(userId) {
    return this.request(`/players/my-memberships?userId=${encodeURIComponent(userId)}`);
  }

  async getAllPlayers(viewerUserId) {
    return this.request(this.withViewerQuery('/players', viewerUserId));
  }

  // Team APIs
  async createTeam(teamData, userId, isAdmin) {
    const params = new URLSearchParams();
    if (userId) params.append('userId', userId);
    if (isAdmin) params.append('isAdmin', 'true');
    const queryString = params.toString();
    const url = `/teams${queryString ? '?' + queryString : ''}`;
    return this.request(url, {
      method: 'POST',
      body: teamData,
    });
  }

  async getTeamById(id) {
    return this.request(`/teams/${id}`);
  }

  async getTeamsByPlayerId(playerId) {
    return this.request(`/teams/player/${playerId}`);
  }

  async getAllTeams() {
    return this.request('/teams');
  }

  // Auth APIs
  async registerUser(userData) {
    // Account-only portal signup; returns { token, user } like login.
    return this.request('/auth/register', {
      method: 'POST',
      body: userData,
    });
  }

  async signup(signupData) {
    return this.request('/auth/signup', {
      method: 'POST',
      body: signupData,
    });
  }

  async login(credentials) {
    return this.request('/auth/login', {
      method: 'POST',
      body: credentials,
    });
  }

  async forgotPassword({ email }) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: { email },
    });
  }

  async changePassword({ userId, currentPassword, newPassword }) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: { userId, currentPassword, newPassword },
    });
  }

  /** Public — uses token from admin-generated reset link */
  async resetPasswordWithToken({ token, newPassword }) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: { token, newPassword },
    });
  }

  async updateTeam(id, teamData, userId, isAdmin) {
    return this.request(`/teams/${id}?userId=${userId || ''}&isAdmin=${isAdmin}`, {
      method: 'PUT',
      body: teamData,
    });
  }

  async addPlayersToTeam(id, playerIds, userId, isAdmin) {
    return this.request(`/teams/${id}/players?userId=${userId || ''}&isAdmin=${isAdmin}`, {
      method: 'POST',
      body: { playerIds },
    });
  }

  async removePlayerFromTeam(teamId, playerId, userId, isAdmin = false) {
    return this.request(`/teams/${teamId}/players/${playerId}?userId=${userId || ''}&isAdmin=${isAdmin}`, {
      method: 'DELETE',
    });
  }

  async updateTeamPlayerSkill(teamId, playerId, skillLevel, userId, isAdmin = false) {
    return this.request(
      `/teams/${teamId}/players/${playerId}/skill?skillLevel=${skillLevel}&userId=${userId || ''}&isAdmin=${isAdmin}`,
      { method: 'PUT' }
    );
  }

  async reshuffleTeams(teamId, userId, isAdmin = false) {
    return this.request(`/teams/${teamId}/reshuffle?userId=${userId || ''}&isAdmin=${isAdmin}`, {
      method: 'POST',
    });
  }

  async movePlayerBetweenTeams(teamId, playerId, fromDivisionIndex, toDivisionIndex, userId, isAdmin = false) {
    return this.request(`/teams/${teamId}/move-player?playerId=${playerId}&fromDivisionIndex=${fromDivisionIndex}&toDivisionIndex=${toDivisionIndex}&userId=${userId || ''}&isAdmin=${isAdmin}`, {
      method: 'POST',
    });
  }

  async updateTeamFormation(teamId, divisionIndex, formation, userId, isAdmin = false) {
    const params = new URLSearchParams({
      divisionIndex: divisionIndex.toString(),
      userId: userId || '',
      isAdmin: isAdmin.toString()
    });
    if (formation) {
      params.append('formation', formation);
    }
    return this.request(`/teams/${teamId}/formation?${params.toString()}`, {
      method: 'PUT',
    });
  }

  async updateTeamFieldPositions(teamId, customFieldPositions, userId, isAdmin = false) {
    return this.request(
      `/teams/${teamId}/field-positions?userId=${encodeURIComponent(userId || '')}&isAdmin=${isAdmin}`,
      {
        method: 'PUT',
        body: JSON.stringify({ customFieldPositions }),
      }
    );
  }

  async ensurePracticeFormationShare(practiceId, userId) {
    return this.request(
      `/practices/${practiceId}/formation-share?userId=${encodeURIComponent(userId)}`,
      { method: 'POST' }
    );
  }

  async setTeamsPublishedToPublic(practiceId, userId, published) {
    return this.request(
      `/practices/${practiceId}/teams-public?userId=${encodeURIComponent(userId)}&published=${published}`,
      { method: 'PUT' }
    );
  }

  async getPublicPracticeFormation(shareToken) {
    return this.request(`/public/practice-formation/${shareToken}`);
  }

  async getPublicFormationPlayerProfile(shareToken, playerId, viewerUserId) {
    let url = `/public/practice-formation/${encodeURIComponent(shareToken)}/players/${encodeURIComponent(playerId)}`;
    if (viewerUserId) {
      url += `?viewerUserId=${encodeURIComponent(viewerUserId)}`;
    }
    return this.request(url);
  }

  getWhatsAppShareUrl(message) {
    return `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
  }

  sharePracticeFormationViaWhatsApp({ title, practiceDate, location, shareToken, clubName, teamsFormed = false }) {
    const shareUrl = soccerUrl(`/public/practice-formation/${shareToken}`);
    let message = teamsFormed
      ? `⚽ *${title || 'Practice'} – Teams & Availability*\n\n`
      : `⚽ *${title || 'Practice'} – Availability*\n\n`;
    if (clubName) {
      message += `🏟 ${clubName}\n`;
    }
    if (practiceDate) {
      const date = new Date(practiceDate);
      message += `📅 ${date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })} at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}\n`;
    }
    if (location) {
      message += `📍 ${location}\n`;
    }
    message += teamsFormed
      ? `\n👉 View player votes, teams & field layout:\n${shareUrl}`
      : `\n👉 View who's in and vote status:\n${shareUrl}`;
    return this.getWhatsAppShareUrl(message);
  }

  sharePublicPracticePageViaWhatsApp({ title, practiceDate, location, shareToken, clubName, teamsFormed = false }) {
    return this.sharePracticeFormationViaWhatsApp({
      title,
      practiceDate,
      location,
      shareToken,
      clubName,
      teamsFormed,
    });
  }

  async getPracticeScrimmageStats(practiceId, userId) {
    return this.request(
      `/practices/${practiceId}/scrimmage-stats?userId=${encodeURIComponent(userId)}`
    );
  }

  async addScrimmageGoal(practiceId, userId, goalData) {
    return this.request(
      `/practices/${practiceId}/scrimmage-stats/goals?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: goalData }
    );
  }

  async removeScrimmageGoal(practiceId, goalId, userId) {
    return this.request(
      `/practices/${practiceId}/scrimmage-stats/goals/${goalId}?userId=${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );
  }

  async incrementScrimmageStat(practiceId, userId, playerId, statType) {
    return this.request(
      `/practices/${practiceId}/scrimmage-stats/player-stat?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: { playerId, statType } }
    );
  }

  async decrementScrimmageStat(practiceId, userId, playerId, statType) {
    return this.request(
      `/practices/${practiceId}/scrimmage-stats/player-stat/decrement?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: { playerId, statType } }
    );
  }

  async resetScrimmageStats(practiceId, userId) {
    const resetUrl = `/practices/${practiceId}/scrimmage-stats/reset?userId=${encodeURIComponent(userId)}`;
    const deleteUrl = `/practices/${practiceId}/scrimmage-stats?userId=${encodeURIComponent(userId)}`;

    try {
      return await this.request(resetUrl, { method: 'POST', body: {} });
    } catch (err) {
      if (!this.isEndpointNotFoundError(err)) {
        throw err;
      }
    }

    try {
      return await this.request(deleteUrl, { method: 'DELETE' });
    } catch (err) {
      if (!this.isEndpointNotFoundError(err)) {
        throw err;
      }
    }

    return this.resetScrimmageStatsClientSide(practiceId, userId);
  }

  isEndpointNotFoundError(err) {
    const message = err?.message || '';
    return message.includes('Endpoint not found') || message.includes('Scrimmage stats endpoint not found');
  }

  isTournamentApiUnavailable(err) {
    const message = err?.message || '';
    return message.includes('The requested item was not found') && message.length < 80;
  }

  async resetScrimmageStatsClientSide(practiceId, userId) {
    let stats = await this.getPracticeScrimmageStats(practiceId, userId);

    for (const goal of [...(stats?.goals || [])]) {
      stats = await this.removeScrimmageGoal(practiceId, goal.id, userId);
    }

    for (const player of stats?.playerStats || []) {
      const statTypes = [
        ['SAVE', player.saves],
        ['DEFENDED', player.defended],
        ['ASSIST', player.assists],
      ];
      for (const [statType, count] of statTypes) {
        for (let i = 0; i < count; i += 1) {
          try {
            stats = await this.decrementScrimmageStat(practiceId, userId, player.playerId, statType);
          } catch (err) {
            if (this.isEndpointNotFoundError(err)) {
              break;
            }
            throw err;
          }
        }
      }
    }

    return stats;
  }

  async finalizeScrimmageStats(practiceId, userId) {
    return this.request(
      `/practices/${practiceId}/scrimmage-stats/finalize?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: {} }
    );
  }

  async ensureScrimmageShare(practiceId, userId) {
    return this.request(
      `/practices/${practiceId}/scrimmage-stats/share?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: {} }
    );
  }

  async getPublicScrimmageStats(shareToken) {
    return this.request(`/public/scrimmage-stats/${shareToken}`);
  }

  shareScrimmageStatsViaWhatsApp({ title, practiceDate, location, shareToken, scoreRed, scoreBlue, goals }) {
    const shareUrl = soccerUrl(`/public/scrimmage-stats/${shareToken}`);
    let message = `⚽ *${title || 'Practice scrimmage'}*\n\n`;
    if (practiceDate) {
      const date = new Date(practiceDate);
      message += `📅 ${date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}\n`;
    }
    if (location) {
      message += `📍 ${location}\n`;
    }
    message += `\n🔴 Red ${scoreRed ?? 0} – ${scoreBlue ?? 0} Blue 🔵\n`;
    if (goals && goals.length > 0) {
      message += `\n*Goals:*\n`;
      [...goals]
        .sort((a, b) => (a.minute ?? 0) - (b.minute ?? 0))
        .forEach((g) => {
          const team = g.teamSide === 'RED' ? '🔴' : '🔵';
          const assist = g.assistPlayerName ? ` (assist: ${g.assistPlayerName})` : '';
          message += `${g.minute}' ${g.scorerPlayerName}${assist} ${team}\n`;
        });
    }
    message += `\n👉 Full stats:\n${shareUrl}`;
    return this.getWhatsAppShareUrl(message);
  }

  // User APIs
  async createUser(userData) {
    return this.request('/users', {
      method: 'POST',
      body: userData,
    });
  }

  async getUserById(id) {
    return this.request(`/users/${id}`);
  }

  async getAllUsers() {
    return this.request('/users');
  }

  async getUsersByRole(role) {
    return this.request(`/users/role/${role}`);
  }

  // Admin APIs
  async deleteTeam(id) {
    return this.request(`/admin/teams/${id}`, {
      method: 'DELETE',
    });
  }

  async approveCreator(userId) {
    console.log('API: Approving creator with userId:', userId);
    try {
      const response = await this.request(`/admin/creators/${userId}/approve`, {
        method: 'POST',
      });
      console.log('API: Approve creator response:', response);
      return response;
    } catch (error) {
      console.error('API: Error in approveCreator:', error);
      throw error;
    }
  }

  async revokeCreatorApproval(userId) {
    return this.request(`/admin/creators/${userId}/revoke`, {
      method: 'POST',
    });
  }

  async getAllCreators() {
    return this.request('/admin/creators');
  }

  async lockTeamForCreator(teamId, creatorUserId) {
    return this.request(`/admin/teams/${teamId}/lock?creatorUserId=${creatorUserId}`, {
      method: 'POST',
    });
  }

  async unlockTeam(teamId) {
    return this.request(`/admin/teams/${teamId}/unlock`, {
      method: 'POST',
    });
  }

  // Admin Player Management
  async getAllPlayersAdmin() {
    return this.request('/admin/players');
  }

  async addPlayerAdmin(playerData) {
    return this.request('/admin/players', {
      method: 'POST',
      body: playerData,
    });
  }

  async updatePlayerAdmin(playerId, playerData) {
    return this.request(`/admin/players/${playerId}`, {
      method: 'PUT',
      body: playerData,
    });
  }

  async deletePlayerAdmin(playerId) {
    return this.request(`/admin/players/${playerId}`, {
      method: 'DELETE',
    });
  }

  async approvePlayerAdmin(playerId) {
    return this.request(`/admin/players/${playerId}/approve`, {
      method: 'PUT',
    });
  }

  async rejectPlayerAdmin(playerId) {
    return this.request(`/admin/players/${playerId}/reject`, {
      method: 'PUT',
    });
  }

  async getPendingPlayers() {
    return this.request('/admin/players/pending-approval');
  }

  async addPlayerToClubAdmin(clubId, playerId) {
    return this.request(`/admin/clubs/${clubId}/players/${playerId}/add`, {
      method: 'POST',
    });
  }

  async getAllClubsAdmin() {
    return this.request('/admin/clubs');
  }

  async createClubAdmin(clubData, adminUserId) {
    return this.request(`/admin/clubs?adminUserId=${adminUserId}`, {
      method: 'POST',
      body: clubData,
    });
  }

  async updateClubAdmin(clubId, clubData) {
    return this.request(`/admin/clubs/${clubId}`, {
      method: 'PUT',
      body: clubData,
    });
  }

  async assignClubAdmin(clubId, userId) {
    return this.request(`/admin/clubs/${clubId}/assign-admin?userId=${encodeURIComponent(userId)}`, {
      method: 'PUT',
    });
  }

  async deleteClubAdmin(clubId) {
    return this.request(`/admin/clubs/${clubId}`, {
      method: 'DELETE',
    });
  }

  async updatePlayer(playerId, playerData, userId) {
    const query = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    return this.request(`/players/${playerId}${query}`, {
      method: 'PUT',
      body: playerData,
    });
  }

  // Admin User Management
  async getAllUsersAdmin() {
    return this.request('/admin/users');
  }

  async assignCreatorRole(userId) {
    return this.request(`/admin/users/${userId}/assign-creator`, {
      method: 'POST',
    });
  }

  async removeCreatorRole(userId) {
    return this.request(`/admin/users/${userId}/remove-creator`, {
      method: 'POST',
    });
  }

  // User CRUD Operations
  async createUserAdmin(userData) {
    return this.request('/admin/users', {
      method: 'POST',
      body: userData,
    });
  }

  async updateUserAdmin(userId, userData) {
    return this.request(`/admin/users/${userId}`, {
      method: 'PUT',
      body: userData,
    });
  }

  async adminGeneratePasswordResetLink(userId) {
    return this.request(`/admin/users/${userId}/password-reset-link`, {
      method: 'POST',
    });
  }

  async deleteUserAdmin(userId) {
    return this.request(`/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Club Management
  async registerClub(clubData, userId) {
    return this.request(`/clubs?userId=${userId}`, {
      method: 'POST',
      body: clubData,
    });
  }

  async getClubById(clubId) {
    return this.request(`/clubs/${clubId}`);
  }

  async getMyClubs(userId) {
    return this.request(`/clubs/my-clubs?userId=${userId}`);
  }

  async getClubByToken(token) {
    return this.request(`/clubs/registration/${token}`);
  }

  async getApprovedClubs() {
    return this.request('/clubs/approved');
  }

  async getPendingClubsForUser(userId) {
    return this.request(`/clubs/pending?userId=${encodeURIComponent(userId)}`);
  }

  // Club Admin Operations
  async approveClub(clubId) {
    return this.request(`/admin/clubs/${clubId}/approve`, {
      method: 'PUT',
    });
  }

  async rejectClub(clubId) {
    return this.request(`/admin/clubs/${clubId}/reject`, {
      method: 'PUT',
    });
  }

  async getPendingClubs() {
    return this.request('/admin/clubs/pending-approval');
  }

  async updateClub(clubId, clubData, userId) {
    return this.request(`/clubs/${clubId}?userId=${userId}`, {
      method: 'PUT',
      body: clubData,
    });
  }

  async uploadClubImage(clubId, imageType, file, userId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('imageType', imageType);

    const url = `${API_BASE_URL}/clubs/${clubId}/images?userId=${encodeURIComponent(userId)}`;
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to upload club image');
    }
    return response.json();
  }

  async generateRegistrationLink(clubId, userId) {
    return this.request(`/clubs/${clubId}/generate-link?userId=${userId}`, {
      method: 'POST',
    });
  }

  async getClubMembers(clubId, status = null, viewerUserId) {
    let url = status
      ? `/clubs/${clubId}/members?status=${status}`
      : `/clubs/${clubId}/members`;
    url = this.withViewerQuery(url, viewerUserId);
    return this.request(url);
  }

  async getPlayerClubProfile(clubId, playerId, viewerUserId) {
    return this.request(
      `/clubs/${clubId}/players/${playerId}/profile?viewerUserId=${encodeURIComponent(viewerUserId)}`
    );
  }

  async getPlayerAchievements(clubId, playerId, userId) {
    return this.request(
      `/clubs/${clubId}/players/${playerId}/achievements?userId=${encodeURIComponent(userId)}`
    );
  }

  async addPlayerAchievement(clubId, playerId, userId, achievementData) {
    return this.request(
      `/clubs/${clubId}/players/${playerId}/achievements?userId=${encodeURIComponent(userId)}`,
      {
        method: 'POST',
        body: achievementData,
      }
    );
  }

  async deletePlayerAchievement(clubId, playerId, achievementId, userId) {
    return this.request(
      `/clubs/${clubId}/players/${playerId}/achievements/${achievementId}?userId=${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );
  }

  async requestClubMembership(clubId, playerId) {
    return this.request(`/clubs/${clubId}/members/request?playerId=${playerId}`, {
      method: 'POST',
    });
  }

  async approvePlayer(clubId, playerId, userId) {
    return this.request(`/clubs/${clubId}/members/${playerId}/approve?userId=${userId}`, {
      method: 'PUT',
    });
  }

  async rejectPlayer(clubId, playerId, userId) {
    return this.request(`/clubs/${clubId}/members/${playerId}/reject?userId=${userId}`, {
      method: 'PUT',
    });
  }

  async assignCoordinatorRole(clubId, playerId, role, userId) {
    return this.request(`/clubs/${clubId}/members/${playerId}/assign-role?role=${role}&userId=${userId}`, {
      method: 'PUT',
    });
  }

  async removeCoordinatorRole(clubId, playerId, role, userId) {
    return this.request(`/clubs/${clubId}/members/${playerId}/remove-role?role=${role}&userId=${userId}`, {
      method: 'PUT',
    });
  }

  async getPracticeSpecialist(clubId) {
    const data = await this.request(`/clubs/${clubId}/practice-specialist`);
    return data?.playerId ? data : null;
  }

  async setPracticeSpecialist(clubId, playerId, userId) {
    return this.request(
      `/clubs/${clubId}/practice-specialist?playerId=${encodeURIComponent(playerId)}&userId=${encodeURIComponent(userId)}`,
      { method: 'PUT' }
    );
  }

  async removePracticeSpecialist(clubId, userId) {
    return this.request(`/clubs/${clubId}/practice-specialist?userId=${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    });
  }

  // Club Player Management
  async getClubPlayers(clubId, userId) {
    return this.request(`/clubs/${clubId}/players?userId=${userId}`);
  }

  async exportClubPlayerRatings(clubId, userId) {
    return this.request(`/clubs/${clubId}/players/ratings-export?userId=${encodeURIComponent(userId)}`);
  }

  async addClubPlayer(clubId, userId, playerData, addToClub = false) {
    return this.request(`/clubs/${clubId}/players?userId=${userId}&addToClub=${addToClub}`, {
      method: 'POST',
      body: playerData,
    });
  }

  async updateClubPlayer(clubId, playerId, userId, playerData) {
    return this.request(`/clubs/${clubId}/players/${playerId}?userId=${userId}`, {
      method: 'PUT',
      body: playerData,
    });
  }

  async deleteClubPlayer(clubId, playerId, userId, removeFromClubOnly = false) {
    return this.request(`/clubs/${clubId}/players/${playerId}?userId=${userId}&removeFromClubOnly=${removeFromClubOnly}`, {
      method: 'DELETE',
    });
  }

  async updateClubSettings(clubId, allowNewMembers, userId) {
    return this.request(`/clubs/${clubId}/settings?userId=${userId}&allowNewMembers=${allowNewMembers}`, {
      method: 'PUT',
    });
  }

  // Anonymous player ratings (club members rate; club/admin view summary)
  async ratePlayer(clubId, playerId, userId, rating) {
    return this.request(`/clubs/${clubId}/players/${playerId}/rate?userId=${encodeURIComponent(userId)}`, {
      method: 'POST',
      body: { rating },
    });
  }

  async getMyRatingForPlayer(clubId, playerId, userId) {
    const res = await this.request(`/clubs/${clubId}/players/${playerId}/my-rating?userId=${encodeURIComponent(userId)}`);
    return res.rating === 0 ? null : res.rating;
  }

  async getPlayerRatingSummary(clubId, playerId, userId) {
    return this.request(`/clubs/${clubId}/players/${playerId}/ratings?userId=${encodeURIComponent(userId)}`);
  }

  // Player Registration via Link
  async registerPlayerViaLink(token, playerData) {
    return this.request(`/player-registration/${token}`, {
      method: 'POST',
      body: playerData,
    });
  }

  // Match Management
  async createMatch(clubId, matchData, userId) {
    return this.request(`/clubs/${clubId}/matches?userId=${userId}`, {
      method: 'POST',
      body: matchData,
    });
  }

  async getMatchesByClub(clubId) {
    return this.request(`/clubs/${clubId}/matches`);
  }

  async getPublishedUpcomingMatchesByClub(clubId) {
    return this.request(`/clubs/${clubId}/matches/published-upcoming`);
  }

  async getMatchById(matchId) {
    return this.request(`/matches/${matchId}`);
  }

  async updateMatch(matchId, matchData, userId) {
    return this.request(`/matches/${matchId}?userId=${userId}`, {
      method: 'PUT',
      body: matchData,
    });
  }

  async cancelMatch(matchId, userId) {
    return this.request(`/matches/${matchId}?userId=${userId}`, {
      method: 'DELETE',
    });
  }

  async updateMatchPreview(matchId, previewData, userId) {
    return this.request(`/matches/${matchId}/preview?userId=${userId}`, {
      method: 'PUT',
      body: previewData,
    });
  }

  async setMatchPublishedToPublic(matchId, userId, published) {
    return this.request(`/matches/${matchId}/publish?userId=${userId}&published=${published}`, {
      method: 'PUT',
    });
  }

  async ensureMatchPreviewShareToken(matchId, userId) {
    return this.request(`/matches/${matchId}/preview-share?userId=${userId}`, {
      method: 'POST',
    });
  }

  async getPublicMatchPreview(shareToken) {
    return this.request(`/public/match-preview/${shareToken}`);
  }

  async voteMatchAvailability(matchId, playerId, availabilityData) {
    return this.request(`/matches/${matchId}/availability?playerId=${playerId}`, {
      method: 'POST',
      body: availabilityData,
    });
  }

  async getMatchAvailability(matchId) {
    return this.request(`/matches/${matchId}/availability`);
  }

  // Practice Management
  async createPractice(clubId, practiceData, userId) {
    return this.request(`/clubs/${clubId}/practices?userId=${userId}`, {
      method: 'POST',
      body: practiceData,
    });
  }

  async getPracticesByClub(clubId) {
    return this.request(`/clubs/${clubId}/practices`);
  }

  async getUpcomingPracticesByClub(clubId, limit = 5) {
    return this.request(`/clubs/${clubId}/practices/upcoming?limit=${limit}`);
  }

  async getCompletedPracticesByClub(clubId, limit = 50) {
    return this.request(`/clubs/${clubId}/practices/completed?limit=${limit}`);
  }

  async getUpcomingPracticesForUser(userId, limit = 10) {
    return this.request(`/users/${userId}/upcoming-practices?limit=${limit}`);
  }

  async createPracticeSeries(clubId, seriesData, userId) {
    return this.request(`/clubs/${clubId}/practice-series?userId=${userId}`, {
      method: 'POST',
      body: seriesData,
    });
  }

  async getPracticeSeriesByClub(clubId) {
    return this.request(`/clubs/${clubId}/practice-series`);
  }

  async updatePracticeSeries(seriesId, seriesData, userId) {
    return this.request(`/practice-series/${seriesId}?userId=${userId}`, {
      method: 'PUT',
      body: seriesData,
    });
  }

  async deletePracticeSeries(seriesId, userId, deleteAll = true) {
    return this.request(`/practice-series/${seriesId}?userId=${userId}&deleteAll=${deleteAll}`, {
      method: 'DELETE',
    });
  }

  async resolvePracticeVoteToken(token) {
    return this.request(`/public/practice-vote/${encodeURIComponent(token)}`);
  }

  async votePracticeWithToken(token, availabilityData) {
    return this.request(`/public/practice-vote/${encodeURIComponent(token)}`, {
      method: 'POST',
      body: availabilityData,
    });
  }

  async generatePracticeVoteTokens(practiceId, userId) {
    return this.request(
      `/practices/${practiceId}/vote-tokens?userId=${encodeURIComponent(userId)}`,
      { method: 'POST' }
    );
  }

  async getPracticeVoteTokens(practiceId, userId) {
    return this.request(
      `/practices/${practiceId}/vote-tokens?userId=${encodeURIComponent(userId)}`
    );
  }

  async ensurePracticeVoteTokenForPlayer(practiceId, playerId, userId) {
    return this.request(
      `/practices/${practiceId}/vote-tokens/${encodeURIComponent(playerId)}?userId=${encodeURIComponent(userId)}`,
      { method: 'POST' }
    );
  }

  async getPracticeById(practiceId, viewerUserId) {
    return this.request(this.withViewerQuery(`/practices/${practiceId}`, viewerUserId));
  }

  async getPracticeLineup(practiceId, userId) {
    return this.request(`/practices/${practiceId}/lineup?userId=${encodeURIComponent(userId)}`);
  }

  async updatePractice(practiceId, practiceData, userId) {
    return this.request(`/practices/${practiceId}?userId=${userId}`, {
      method: 'PUT',
      body: practiceData,
    });
  }

  async updatePracticePlan(practiceId, planData, userId) {
    return this.request(`/practices/${practiceId}/practice-plan?userId=${encodeURIComponent(userId)}`, {
      method: 'PUT',
      body: planData,
    });
  }

  async publishPracticePlan(practiceId, userId) {
    return this.request(`/practices/${practiceId}/practice-plan/publish?userId=${encodeURIComponent(userId)}`, {
      method: 'PUT',
    });
  }

  async getPracticePlanTemplate(practiceId, userId) {
    return this.request(`/practices/${practiceId}/practice-plan/template?userId=${encodeURIComponent(userId)}`);
  }

  async duplicatePracticePlanFromLast(practiceId, userId) {
    return this.request(`/practices/${practiceId}/practice-plan/duplicate-from-last?userId=${encodeURIComponent(userId)}`, {
      method: 'POST',
    });
  }

  async deletePractice(practiceId, userId) {
    return this.request(`/practices/${practiceId}?userId=${userId}`, {
      method: 'DELETE',
    });
  }

  async cancelPractice(practiceId, userId) {
    return this.request(`/practices/${practiceId}/cancel?userId=${userId}`, {
      method: 'PUT',
    });
  }

  async votePracticeAvailability(practiceId, playerId, availabilityData, viewerUserId) {
    const url = this.withViewerQuery(`/practices/${practiceId}/availability?playerId=${encodeURIComponent(playerId)}`, viewerUserId);
    return this.request(url, {
      method: 'POST',
      body: availabilityData,
    });
  }

  async getPracticeAvailability(practiceId, viewerUserId) {
    return this.request(this.withViewerQuery(`/practices/${practiceId}/availability`, viewerUserId));
  }

  async addPlayerToPracticeAvailability(practiceId, playerId, userId, availabilityData = {}) {
    return this.request(`/practices/${practiceId}/availability/add-player?playerId=${playerId}&userId=${userId}`, {
      method: 'POST',
      body: availabilityData,
    });
  }

  async updatePlayerPracticeAvailability(practiceId, playerId, userId, availabilityData) {
    return this.request(`/practices/${practiceId}/availability/${playerId}?userId=${userId}`, {
      method: 'PUT',
      body: availabilityData,
    });
  }

  async removePlayerFromPractice(practiceId, playerId, userId) {
    return this.request(`/practices/${practiceId}/availability/${playerId}?userId=${userId}`, {
      method: 'DELETE',
    });
  }

  async formPracticeTeams(practiceId, userId, playerIds, numberOfTeams) {
    const playerIdsParam = playerIds.join(',');
    return this.request(`/practices/${practiceId}/form-teams?userId=${userId}&playerIds=${playerIdsParam}&numberOfTeams=${numberOfTeams}`, {
      method: 'POST',
    });
  }

  async formTeamsFromRsvps(practiceId, userId) {
    return this.request(`/practices/${practiceId}/form-teams-from-rsvps?userId=${encodeURIComponent(userId)}`, {
      method: 'POST',
    });
  }

  async getPracticeTeams(practiceId) {
    return this.request(`/practices/${practiceId}/teams`);
  }

  // Match Summary APIs
  async createMatchSummary(matchId, summaryData, userId) {
    return this.request(`/matches/${matchId}/summary?userId=${userId}`, {
      method: 'POST',
      body: summaryData,
    });
  }

  async createPracticeSummary(practiceId, summaryData, userId) {
    return this.request(`/practices/${practiceId}/summary?userId=${userId}`, {
      method: 'POST',
      body: summaryData,
    });
  }

  async getMatchSummary(matchId) {
    return this.request(`/matches/${matchId}/summary`);
  }

  async getPracticeSummary(practiceId) {
    return this.request(`/practices/${practiceId}/summary`);
  }

  async syncPracticeSummaryFromScrimmage(practiceId, userId) {
    return this.request(
      `/practices/${practiceId}/summary/sync-from-scrimmage?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: {} }
    );
  }

  async getPublicSummary(shareToken) {
    return this.request(`/public/summary/${shareToken}`);
  }

  async uploadMatchPhotos(matchId, files, userId) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('userId', userId);

    const url = `${API_BASE_URL}/matches/${matchId}/summary/photos?userId=${userId}`;
    const token = localStorage.getItem('authToken');
    const config = {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    };

    const response = await fetch(url, config);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to upload photos');
    }
    return response.json();
  }

  async uploadPracticePhotos(practiceId, files, userId) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('userId', userId);

    const url = `${API_BASE_URL}/practices/${practiceId}/summary/photos?userId=${userId}`;
    const token = localStorage.getItem('authToken');
    const config = {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    };

    const response = await fetch(url, config);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to upload photos');
    }
    return response.json();
  }

  async getMatchParticipants(matchId, viewerUserId) {
    return this.request(this.withViewerQuery(`/matches/${matchId}/participants`, viewerUserId));
  }

  async getPracticeParticipants(practiceId, viewerUserId) {
    return this.request(this.withViewerQuery(`/practices/${practiceId}/participants`, viewerUserId));
  }

  async deleteSummaryPhoto(summaryId, photoUrl) {
    return this.request(`/summary/${summaryId}/photos?photoUrl=${encodeURIComponent(photoUrl)}`, {
      method: 'DELETE',
    });
  }

  // Club blog posts
  async getPublishedPosts() {
    return this.request('/posts');
  }

  async getPublishedClubPosts(clubId) {
    return this.request(`/clubs/${clubId}/posts/published`);
  }

  async getPublishedPost(slug) {
    return this.request(`/posts/${encodeURIComponent(slug)}`);
  }

  async getClubPosts(clubId, userId) {
    return this.request(`/clubs/${clubId}/posts?userId=${encodeURIComponent(userId)}`);
  }

  async getClubPost(clubId, postId, userId) {
    return this.request(`/clubs/${clubId}/posts/${postId}?userId=${encodeURIComponent(userId)}`);
  }

  async createClubPost(clubId, postData, userId) {
    return this.request(`/clubs/${clubId}/posts?userId=${encodeURIComponent(userId)}`, {
      method: 'POST',
      body: postData,
    });
  }

  async createBlogFromPracticeSummary(clubId, practiceId, userId) {
    return this.request(
      `/clubs/${clubId}/blog/from-practice-summary?practiceId=${encodeURIComponent(practiceId)}&userId=${encodeURIComponent(userId)}`,
      { method: 'POST' }
    );
  }

  async updateClubPost(clubId, postId, postData, userId) {
    return this.request(`/clubs/${clubId}/posts/${postId}?userId=${encodeURIComponent(userId)}`, {
      method: 'PUT',
      body: postData,
    });
  }

  async uploadClubPostPhotos(clubId, postId, files, userId) {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    const url = `${API_BASE_URL}/clubs/${clubId}/posts/${postId}/photos?userId=${encodeURIComponent(userId)}`;
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to upload photos');
    }
    return response.json();
  }

  shareSummaryViaWhatsApp(
    shareToken,
    matchTitle,
    scoreHome,
    scoreAway,
    opponentName,
    manOfMatch,
    manOfMatchReason,
    photoUrls,
    nextMatchDate,
    nextMatchTitle,
    scrimmageStats = null
  ) {
    const shareUrl = soccerUrl(`/public/summary/${shareToken}`);
    let message = `⚽ *${matchTitle}*\n\n`;
    
    // Score section
    if (scoreHome !== null && scoreHome !== undefined && scoreAway !== null && scoreAway !== undefined) {
      message += `📊 *Final Score*\n`;
      message += `🔴 Red ${scoreHome} – ${scoreAway} Blue 🔵\n\n`;
    }
    
    // Opponent/Teams section
    if (opponentName) {
      message += `🆚 ${opponentName}\n\n`;
    }

    if (scrimmageStats?.goals?.length > 0) {
      message += `⚽ *Goals*\n`;
      [...scrimmageStats.goals]
        .sort((a, b) => (a.minute ?? 0) - (b.minute ?? 0))
        .forEach((g) => {
          const team = g.teamSide === 'RED' ? '🔴' : '🔵';
          const assist = g.assistPlayerName ? ` (assist: ${g.assistPlayerName})` : '';
          message += `${g.minute}' ${g.scorerPlayerName}${assist} ${team}\n`;
        });
      message += `\n`;
    }

    const topPerformers = (scrimmageStats?.playerStats || [])
      .filter((p) => p.goals + p.assists + p.saves + p.defended > 0)
      .sort((a, b) => {
        const scoreA = a.goals * 10 + a.assists * 5 + a.saves + a.defended;
        const scoreB = b.goals * 10 + b.assists * 5 + b.saves + b.defended;
        return scoreB - scoreA;
      })
      .slice(0, 5);
    if (topPerformers.length > 0) {
      message += `🏅 *Top performers*\n`;
      topPerformers.forEach((p) => {
        const parts = [];
        if (p.goals > 0) parts.push(`${p.goals}G`);
        if (p.assists > 0) parts.push(`${p.assists}A`);
        if (p.saves > 0) parts.push(`${p.saves}SV`);
        if (p.defended > 0) parts.push(`${p.defended}DEF`);
        message += `${p.playerName}: ${parts.join(', ')}\n`;
      });
      message += `\n`;
    }
    
    // Man of the Match section with full details
    if (manOfMatch) {
      message += `⭐ *Man of the Match*\n`;
      message += `*${manOfMatch}*\n`;
      if (manOfMatchReason) {
        message += `\n💬 "${manOfMatchReason}"\n`;
      }
      message += `\n`;
    }
    
    // Photos section - include all photo URLs
    if (photoUrls && photoUrls.length > 0) {
      message += `📸 *Match Photos* (${photoUrls.length} photo${photoUrls.length > 1 ? 's' : ''})\n\n`;
      // Include all photo URLs - WhatsApp will show link previews for image URLs
      photoUrls.forEach((url, index) => {
        message += `${url}\n`;
      });
      message += `\n`;
    }
    
    // Next match section
    if (nextMatchDate) {
      const nextDate = new Date(nextMatchDate);
      const formattedDate = nextDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      message += `📅 *Next Match*\n`;
      if (nextMatchTitle) {
        message += `${nextMatchTitle}\n`;
      }
      message += `${formattedDate}\n\n`;
    }
    
    // Link to full summary
    message += `🔗 *View Full Summary*\n${shareUrl}`;
    
    const encodedMessage = encodeURIComponent(message);
    return `https://api.whatsapp.com/send?text=${encodedMessage}`;
  }

  // Club tournaments
  async getClubTournaments(clubId, userId) {
    return this.request(`/clubs/${clubId}/tournaments?userId=${encodeURIComponent(userId)}`);
  }

  async getPublishedClubTournaments(clubId) {
    return this.request(`/clubs/${clubId}/tournaments/published`);
  }

  async createClubTournament(clubId, userId, data) {
    return this.request(
      `/clubs/${clubId}/tournaments?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: data }
    );
  }

  async getClubTournament(clubId, tournamentId, userId) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}?userId=${encodeURIComponent(userId)}`
    );
  }

  async updateClubTournament(clubId, tournamentId, userId, data) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}?userId=${encodeURIComponent(userId)}`,
      { method: 'PUT', body: data }
    );
  }

  async deleteClubTournament(clubId, tournamentId, userId) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}?userId=${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );
  }

  async addTournamentFixture(clubId, tournamentId, userId, data) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}/fixtures?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: data }
    );
  }

  async updateTournamentFixture(clubId, tournamentId, fixtureId, userId, data) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}/fixtures/${fixtureId}?userId=${encodeURIComponent(userId)}`,
      { method: 'PUT', body: data }
    );
  }

  async endTournamentFixture(clubId, tournamentId, fixtureId, userId) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}/fixtures/${fixtureId}/end?userId=${encodeURIComponent(userId)}`,
      { method: 'POST' }
    );
  }

  async startTournamentFixture(clubId, tournamentId, fixtureId, userId) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}/fixtures/${fixtureId}/start?userId=${encodeURIComponent(userId)}`,
      { method: 'POST' }
    );
  }

  async deleteTournamentFixture(clubId, tournamentId, fixtureId, userId) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}/fixtures/${fixtureId}?userId=${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );
  }

  async addTournamentGoal(clubId, tournamentId, fixtureId, userId, data) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}/fixtures/${fixtureId}/goals?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: data }
    );
  }

  async removeTournamentGoal(clubId, tournamentId, fixtureId, goalId, userId) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}/fixtures/${fixtureId}/goals/${goalId}?userId=${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );
  }

  async publishClubTournament(clubId, tournamentId, userId) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}/publish?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: {} }
    );
  }

  async unpublishClubTournament(clubId, tournamentId, userId) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}/unpublish?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: {} }
    );
  }

  async ensureTournamentShare(clubId, tournamentId, userId) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}/share?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: {} }
    );
  }

  async uploadTournamentTeamPhotos(clubId, tournamentId, files, userId) {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const url = `${API_BASE_URL}/clubs/${clubId}/tournaments/${tournamentId}/photos/team?userId=${encodeURIComponent(userId)}`;
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to upload team photos');
    }
    return response.json();
  }

  async deleteTournamentTeamPhoto(clubId, tournamentId, photoUrl, userId) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}/photos/team?userId=${encodeURIComponent(userId)}&photoUrl=${encodeURIComponent(photoUrl)}`,
      { method: 'DELETE' }
    );
  }

  async uploadTournamentMemberPhoto(clubId, tournamentId, playerId, playerName, file, userId) {
    const formData = new FormData();
    formData.append('file', file);
    let url = `${API_BASE_URL}/clubs/${clubId}/tournaments/${tournamentId}/photos/members?userId=${encodeURIComponent(userId)}&playerId=${encodeURIComponent(playerId)}`;
    if (playerName) {
      url += `&playerName=${encodeURIComponent(playerName)}`;
    }
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to upload member photo');
    }
    return response.json();
  }

  async deleteTournamentMemberPhoto(clubId, tournamentId, playerId, userId) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}/photos/members?userId=${encodeURIComponent(userId)}&playerId=${encodeURIComponent(playerId)}`,
      { method: 'DELETE' }
    );
  }

  async updateTournamentAward(clubId, tournamentId, userId, awardData) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}/awards?userId=${encodeURIComponent(userId)}`,
      { method: 'PUT', body: awardData }
    );
  }

  async uploadTournamentAwardPhoto(clubId, tournamentId, awardType, file, userId) {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${API_BASE_URL}/clubs/${clubId}/tournaments/${tournamentId}/awards/${awardType}/photo?userId=${encodeURIComponent(userId)}`;
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to upload award photo');
    }
    return response.json();
  }

  async deleteTournamentAwardPhoto(clubId, tournamentId, awardType, userId) {
    return this.request(
      `/clubs/${clubId}/tournaments/${tournamentId}/awards/${awardType}/photo?userId=${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );
  }

  async getPublicTournament(shareToken) {
    return this.request(`/public/tournament/${shareToken}`);
  }

  async publishClubStatusBlog(clubId, userId) {
    return this.request(
      `/clubs/${clubId}/blog/auto-status/publish?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: {} }
    );
  }

  shareTournamentViaWhatsApp({ tournamentName, clubName, shareToken, status, fixtures }) {
    const shareUrl = soccerUrl(`/public/tournament/${shareToken}`);
    let message = `🏆 *${tournamentName || 'Tournament'}*\n`;
    if (clubName) message += `${clubName}\n\n`;
    const liveFixtures = (fixtures || []).filter((f) => f.status === 'LIVE');
    const finalFixtures = (fixtures || []).filter((f) => f.status === 'FINAL');
    const upcomingFixtures = (fixtures || []).filter((f) => f.status === 'SCHEDULED');

    if (liveFixtures.length > 0) {
      message += `🔴 *LIVE NOW*\n`;
      liveFixtures.forEach((f) => {
        message += `${f.homeTeam} ${f.scoreHome ?? 0} - ${f.scoreAway ?? 0} ${f.awayTeam}\n`;
      });
      message += `\n`;
    }

    if (finalFixtures.length > 0) {
      message += `✅ *Results*\n`;
      finalFixtures.forEach((f) => {
        message += `${f.homeTeam} ${f.scoreHome ?? 0} - ${f.scoreAway ?? 0} ${f.awayTeam}\n`;
      });
      message += `\n`;
    }

    if (upcomingFixtures.length > 0) {
      message += `📅 *Upcoming*\n`;
      upcomingFixtures.slice(0, 5).forEach((f) => {
        const when = f.matchDate ? new Date(f.matchDate).toLocaleString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }) : 'TBD';
        message += `${when}: ${f.homeTeam} vs ${f.awayTeam}\n`;
      });
      message += `\n`;
    } else if (liveFixtures.length === 0 && finalFixtures.length === 0) {
      message += `⚽ *Scores*\n`;
      (fixtures || []).forEach((f) => {
        message += `${f.homeTeam} ${f.scoreHome ?? 0} - ${f.scoreAway ?? 0} ${f.awayTeam}\n`;
      });
      message += `\n`;
    }

    message += `🔗 *View live scores & stats*\n${shareUrl}`;
    return `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
  }

  // Training Tracker APIs
  async getTrainingTemplates() {
    return this.request('/training/templates');
  }

  async createTrainingPlan(userId, planData) {
    return this.request(`/training/plans?userId=${encodeURIComponent(userId)}`, {
      method: 'POST',
      body: planData,
    });
  }

  async getActiveTrainingPlan(userId) {
    return this.request(`/training/plans/active?userId=${encodeURIComponent(userId)}`);
  }

  async listTrainingPlans(userId) {
    return this.request(`/training/plans?userId=${encodeURIComponent(userId)}`);
  }

  async getTrainingToday(userId) {
    return this.request(`/training/today?userId=${encodeURIComponent(userId)}`);
  }

  async getTrainingProgress(userId) {
    return this.request(`/training/progress?userId=${encodeURIComponent(userId)}`);
  }

  async upsertTrainingLog(userId, logData) {
    return this.request(`/training/logs?userId=${encodeURIComponent(userId)}`, {
      method: 'POST',
      body: logData,
    });
  }

  async listTrainingLogs(userId, from, to) {
    const params = new URLSearchParams({ userId });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return this.request(`/training/logs?${params.toString()}`);
  }

  async getTrainingLog(userId, logId) {
    return this.request(`/training/logs/${encodeURIComponent(logId)}?userId=${encodeURIComponent(userId)}`);
  }

  async uploadTrainingPhotos(userId, logId, files) {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const url = `${API_BASE_URL}/training/logs/${encodeURIComponent(logId)}/photos?userId=${encodeURIComponent(userId)}`;
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Failed to upload photos');
    }
    return response.json();
  }

  async updateTrainingShare(userId, logId, visibility = 'PUBLIC') {
    return this.request(
      `/training/logs/${encodeURIComponent(logId)}/share?userId=${encodeURIComponent(userId)}`,
      {
        method: 'POST',
        body: { visibility },
      }
    );
  }

  async getPublicTrainingLog(shareToken) {
    return this.request(`/public/training/${encodeURIComponent(shareToken)}`);
  }

  // Running club — routes
  async createRunningRoute(clubId, routeData, userId) {
    return this.request(`/clubs/${clubId}/running-routes?userId=${encodeURIComponent(userId)}`, {
      method: 'POST',
      body: routeData,
    });
  }

  async getRunningRoutes(clubId, publishedOnly = false) {
    return this.request(
      `/clubs/${clubId}/running-routes?publishedOnly=${publishedOnly ? 'true' : 'false'}`
    );
  }

  async getRunningRoute(routeId) {
    return this.request(`/running-routes/${routeId}`);
  }

  async updateRunningRoute(routeId, routeData, userId) {
    return this.request(`/running-routes/${routeId}?userId=${encodeURIComponent(userId)}`, {
      method: 'PUT',
      body: routeData,
    });
  }

  async publishRunningRoute(routeId, userId, published = true) {
    return this.request(
      `/running-routes/${routeId}/publish?userId=${encodeURIComponent(userId)}&published=${published}`,
      { method: 'PUT' }
    );
  }

  async deleteRunningRoute(routeId, userId) {
    return this.request(`/running-routes/${routeId}?userId=${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    });
  }

  async uploadRunningRoutePhotos(routeId, files, userId) {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const url = `${API_BASE_URL}/running-routes/${routeId}/photos?userId=${encodeURIComponent(userId)}`;
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to upload route photos');
    }
    return response.json();
  }

  // Running club — practices
  async createRunningPractice(clubId, practiceData, userId) {
    return this.request(`/clubs/${clubId}/running-practices?userId=${encodeURIComponent(userId)}`, {
      method: 'POST',
      body: practiceData,
    });
  }

  async getRunningPractices(clubId) {
    return this.request(`/clubs/${clubId}/running-practices`);
  }

  async getUpcomingRunningPractices(clubId, limit = 5) {
    return this.request(`/clubs/${clubId}/running-practices/upcoming?limit=${limit}`);
  }

  async getRunningPractice(practiceId) {
    return this.request(`/running-practices/${practiceId}`);
  }

  async updateRunningPractice(practiceId, practiceData, userId) {
    return this.request(`/running-practices/${practiceId}?userId=${encodeURIComponent(userId)}`, {
      method: 'PUT',
      body: practiceData,
    });
  }

  async cancelRunningPractice(practiceId, userId) {
    return this.request(`/running-practices/${practiceId}/cancel?userId=${encodeURIComponent(userId)}`, {
      method: 'PUT',
    });
  }

  async shareRunningPractice(practiceId, userId) {
    return this.request(`/running-practices/${practiceId}/share?userId=${encodeURIComponent(userId)}`, {
      method: 'POST',
    });
  }

  async uploadRunningPracticePhotos(practiceId, files, userId) {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const url = `${API_BASE_URL}/running-practices/${practiceId}/photos?userId=${encodeURIComponent(userId)}`;
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to upload practice photos');
    }
    return response.json();
  }

  async deleteRunningPracticePhoto(practiceId, photoUrl, userId) {
    return this.request(
      `/running-practices/${practiceId}/photos?userId=${encodeURIComponent(userId)}&photoUrl=${encodeURIComponent(photoUrl)}`,
      { method: 'DELETE' }
    );
  }

  async voteRunningPracticeAvailability(practiceId, playerId, availabilityData, viewerUserId) {
    let url = `/running-practices/${practiceId}/availability?playerId=${encodeURIComponent(playerId)}`;
    if (viewerUserId) {
      url += `&viewerUserId=${encodeURIComponent(viewerUserId)}`;
    }
    return this.request(url, {
      method: 'POST',
      body: availabilityData,
    });
  }

  async getRunningPracticeAvailability(practiceId, viewerUserId) {
    let url = `/running-practices/${practiceId}/availability`;
    if (viewerUserId) {
      url += `?viewerUserId=${encodeURIComponent(viewerUserId)}`;
    }
    return this.request(url);
  }

  async getPublicRunningPractice(shareToken) {
    return this.request(`/public/running-practices/${encodeURIComponent(shareToken)}`);
  }

  shareRunningPracticeViaWhatsApp({ title, practiceDate, gatheringLocation, routeTitle, shareUrl }) {
    const when = practiceDate
      ? new Date(practiceDate).toLocaleString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : 'TBA';
    const message =
      `🏃 *${title || 'Group run'}*\n` +
      `🕒 ${when}\n` +
      (gatheringLocation ? `📍 Meet: ${gatheringLocation}\n` : '') +
      (routeTitle ? `🗺️ Route: ${routeTitle}\n` : '') +
      `\n🔗 ${shareUrl}`;
    return `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
  }

  // Hiker profile
  async getMyHikerProfile(userId) {
    return this.request(`/hikers/my-profile?userId=${encodeURIComponent(userId)}`);
  }

  async saveHikerProfile(userId, profileData, { create = false } = {}) {
    return this.request(`/hikers/my-profile?userId=${encodeURIComponent(userId)}`, {
      method: create ? 'POST' : 'PUT',
      body: profileData,
    });
  }

  async uploadHikerProfilePicture(file) {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${API_BASE_URL}/hikers/upload-profile-picture`;
    const token = localStorage.getItem('authToken');

    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) {
      throw new Error('Failed to upload profile picture');
    }
    const data = await response.json();
    return data.url;
  }

  // Hiking club — trails
  async createHikingTrail(clubId, trailData, userId) {
    return this.request(`/clubs/${clubId}/hiking-trails?userId=${encodeURIComponent(userId)}`, {
      method: 'POST',
      body: trailData,
    });
  }

  async getHikingTrails(clubId, publishedOnly = false) {
    return this.request(
      `/clubs/${clubId}/hiking-trails?publishedOnly=${publishedOnly ? 'true' : 'false'}`
    );
  }

  async getHikingTrail(trailId) {
    return this.request(`/hiking-trails/${trailId}`);
  }

  async updateHikingTrail(trailId, trailData, userId) {
    return this.request(`/hiking-trails/${trailId}?userId=${encodeURIComponent(userId)}`, {
      method: 'PUT',
      body: trailData,
    });
  }

  async publishHikingTrail(trailId, userId, published = true) {
    return this.request(
      `/hiking-trails/${trailId}/publish?userId=${encodeURIComponent(userId)}&published=${published}`,
      { method: 'PUT' }
    );
  }

  async deleteHikingTrail(trailId, userId) {
    return this.request(`/hiking-trails/${trailId}?userId=${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    });
  }

  async uploadHikingTrailPhotos(trailId, files, userId) {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const url = `${API_BASE_URL}/hiking-trails/${trailId}/photos?userId=${encodeURIComponent(userId)}`;
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) {
      throw new Error('Failed to upload trail photos');
    }
    return response.json();
  }

  // Hiking club — outings
  async createHikingOuting(clubId, outingData, userId) {
    return this.request(`/clubs/${clubId}/hiking-outings?userId=${encodeURIComponent(userId)}`, {
      method: 'POST',
      body: outingData,
    });
  }

  async getHikingOutings(clubId) {
    return this.request(`/clubs/${clubId}/hiking-outings`);
  }

  async getUpcomingHikingOutings(clubId, limit = 5) {
    return this.request(`/clubs/${clubId}/hiking-outings/upcoming?limit=${limit}`);
  }

  async getHikingOuting(outingId) {
    return this.request(`/hiking-outings/${outingId}`);
  }

  async updateHikingOuting(outingId, outingData, userId) {
    return this.request(`/hiking-outings/${outingId}?userId=${encodeURIComponent(userId)}`, {
      method: 'PUT',
      body: outingData,
    });
  }

  async cancelHikingOuting(outingId, userId) {
    return this.request(`/hiking-outings/${outingId}/cancel?userId=${encodeURIComponent(userId)}`, {
      method: 'PUT',
    });
  }

  async shareHikingOuting(outingId, userId) {
    return this.request(`/hiking-outings/${outingId}/share?userId=${encodeURIComponent(userId)}`, {
      method: 'POST',
    });
  }

  async uploadHikingOutingPhotos(outingId, files, userId) {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const url = `${API_BASE_URL}/hiking-outings/${outingId}/photos?userId=${encodeURIComponent(userId)}`;
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) {
      throw new Error('Failed to upload outing photos');
    }
    return response.json();
  }

  async deleteHikingOutingPhoto(outingId, photoUrl, userId) {
    return this.request(
      `/hiking-outings/${outingId}/photos?userId=${encodeURIComponent(userId)}&photoUrl=${encodeURIComponent(photoUrl)}`,
      { method: 'DELETE' }
    );
  }

  async voteHikingOutingAvailability(outingId, playerId, availabilityData, viewerUserId) {
    let url = `/hiking-outings/${outingId}/availability?playerId=${encodeURIComponent(playerId)}`;
    if (viewerUserId) {
      url += `&viewerUserId=${encodeURIComponent(viewerUserId)}`;
    }
    return this.request(url, {
      method: 'POST',
      body: availabilityData,
    });
  }

  async getHikingOutingAvailability(outingId, viewerUserId) {
    let url = `/hiking-outings/${outingId}/availability`;
    if (viewerUserId) {
      url += `?viewerUserId=${encodeURIComponent(viewerUserId)}`;
    }
    return this.request(url);
  }

  async getPublicHikingOuting(shareToken) {
    return this.request(`/public/hiking-outings/${encodeURIComponent(shareToken)}`);
  }

  shareHikingOutingViaWhatsApp({ title, outingDate, meetingLocation, trailTitle, shareUrl }) {
    const when = outingDate
      ? new Date(outingDate).toLocaleString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : 'TBA';
    const message =
      `🥾 *${title || 'Group hike'}*\n` +
      `🕒 ${when}\n` +
      (meetingLocation ? `📍 Meet: ${meetingLocation}\n` : '') +
      (trailTitle ? `🗺️ Trail: ${trailTitle}\n` : '') +
      `\n🔗 ${shareUrl}`;
    return `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
  }

  shareTrainingLogViaWhatsApp({ athleteFirstName, date, completedCount, totalCount, shareToken }) {
    const shareUrl = soccerUrl(`/public/training/${shareToken}`);
    const when = date
      ? new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })
      : 'today';
    const name = athleteFirstName || 'Athlete';
    const message =
      `⚽ *Training check-in*\n` +
      `${name} logged ${completedCount || 0}/${totalCount || 0} sessions for ${when}.\n\n` +
      `🔗 ${shareUrl}`;
    return `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
  }

  async getActiveTrainingChallenge(clubId, userId) {
    try {
      return await this.request(
        `/clubs/${encodeURIComponent(clubId)}/training/challenges/active?userId=${encodeURIComponent(userId)}`
      );
    } catch (err) {
      if (err.message?.includes('not found')) return null;
      throw err;
    }
  }

  async createTrainingChallenge(clubId, userId, data) {
    return this.request(
      `/clubs/${encodeURIComponent(clubId)}/training/challenges?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: data }
    );
  }

  async joinTrainingChallenge(challengeId, userId, data = {}) {
    return this.request(
      `/training/challenges/${encodeURIComponent(challengeId)}/join?userId=${encodeURIComponent(userId)}`,
      { method: 'POST', body: data }
    );
  }

  async getTrainingLeaderboard(clubId, challengeId, userId) {
    return this.request(
      `/clubs/${encodeURIComponent(clubId)}/training/challenges/${encodeURIComponent(challengeId)}/leaderboard?userId=${encodeURIComponent(userId)}`
    );
  }

  async getTrainingClubFeed(clubId, userId) {
    return this.request(`/clubs/${encodeURIComponent(clubId)}/training/feed?userId=${encodeURIComponent(userId)}`);
  }

  async getPublicTrainingChallenge(shareToken) {
    return this.request(`/public/training/challenge/${encodeURIComponent(shareToken)}`);
  }

  async updateTrainingLeaderboardOptIn(userId, showOnLeaderboard) {
    return this.request(
      `/training/leaderboard-opt-in?userId=${encodeURIComponent(userId)}&showOnLeaderboard=${showOnLeaderboard}`,
      { method: 'POST' }
    );
  }

  shareTrainingChallengeViaWhatsApp({ challengeName, clubName, shareToken, participantCount }) {
    const shareUrl = `${getShareOrigin()}${trainingPath(`/public/challenge/${shareToken}`)}`;
    const message =
      `🏋️ *${challengeName || 'Club training challenge'}*\n` +
      `${clubName || 'Club'} · ${participantCount || 0} on leaderboard\n\n` +
      `🔗 ${shareUrl}`;
    return `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
  }
}

const apiService = new ApiService();
export default apiService;

